import React, { useCallback, useState } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import { NavLink as RouterLink, useNavigate } from "react-router-dom";
import logo from "../../assets/logo_dark_small.png";
import {
  Box,
  Flex,
  Avatar,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  keyframes,
  VStack,
  Circle,
  HStack,
  Text,
  Icon,
  Divider,
  IconButton,
  Tooltip,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Stack,
  Button,
  Spinner,
  Input,
} from "@chakra-ui/react";
import { useUser } from "../../context/UserContext";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpandFilled, TbLogout2 } from "react-icons/tb";
import {
  FiSettings,
  FiTrash2,
  FiMoreVertical,
  FiEdit2,
} from "react-icons/fi";
import { HamburgerIcon } from "@chakra-ui/icons";
import { useAnalystChat } from "context/AnalystChatContext";
import { ChatClientType } from "build-ts/chat_session";
import { updateChatSessionHeaders } from "services/AgentChatService";
import showToast from "hooks/useCustomToast";
import { MdAddCircleOutline, MdSupportAgent } from "react-icons/md";
import { IoChatbubbleOutline, IoChatbubblesOutline } from "react-icons/io5";
import { BsRobot } from "react-icons/bs";
import { GiBrain } from "react-icons/gi";

interface NavLink {
  to: string;
  text: string;
  iconComponent: React.ComponentType;
  iconSize: number;
}

export const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  85% { transform: rotate(0deg); }
  100% { transform: rotate(120deg); }
`;

// Memoized Avatar component
interface AvatarWithMemoProps {
  src: string;
  animation: string;
  name: string;
  size?: string;
}

export const AvatarWithMemo: React.FC<AvatarWithMemoProps> = React.memo(
  ({ src, animation, name, size = "xs" }) => {
    return (
      <Avatar
        size={size}
        src={src}
        animation={animation}
        name={name}
        color="black"
      />
    );
  }
);

const Navbar: React.FC = () => {
  const { username, userId, defaultAnalystId } = useUser();
  const animation = `${rotateAnimation} 3s linear infinite`;
  const navigate = useNavigate();
  // const [isCollapsed, setIsCollapsed] = useState(false);
  const mobileDrawer = useDisclosure();

  // Get analyst chat context
  const {
    chatSessionsList,
    currentChatId,
    setCurrentChatId,
    setCurrentSessionHeader,
    isLoadingChatSessions,
    selectChatSession,
    deleteChatSession,
    setSelectedAnalyst,
    analystsList,
    fetchChatSessionsList,
    isNavbarCollapsed,
    setIsNavbarCollapsed,
  } = useAnalystChat();

  // Inline rename state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleRename = async (sessionId: string) => {
    if (!editValue.trim()) {
      setEditingId(null);
      return;
    }
    const success = await updateChatSessionHeaders([sessionId], editValue.trim(), undefined, false);
    if (success) {
      showToast("Chat renamed", "", "success");
      fetchChatSessionsList(ChatClientType.FRIDA, "");
    }
    setEditingId(null);
  };

  // Handle nav link click - select first analyst for New Chat, reset for others
  const handleNavLinkClick = (linkTo: string) => {
    if (linkTo === "/chat/new") {
      // Select first analyst if available when clicking New Chat
      if (analystsList.length > 0) {
        setSelectedAnalyst(analystsList.find(analyst => analyst.id === defaultAnalystId) || analystsList[0]);
      }
    } else {
      // Reset analyst
      setSelectedAnalyst(null);
      // Reset current chat id and session header
      setCurrentChatId("");
      setCurrentSessionHeader(undefined);
    }
  };

  const navLinks: NavLink[] = [
    { to: "/chat/new", text: "New Chat", iconComponent: MdAddCircleOutline, iconSize: 6 },
    { to: "/chat-session", text: "Chats", iconComponent: IoChatbubblesOutline, iconSize: 6 },
    { to: "/analyst", text: "Analysts", iconComponent: BsRobot, iconSize: 6 },
    // { to: "/data-query", text: "Data", iconComponent: FiDatabase, iconSize: 5 },
    { to: "/mkg", text: "Knowledge", iconComponent: GiBrain, iconSize: 6 },
  ];

  const { logout } = useUser();
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const toggleCollapse = () => {
    setIsNavbarCollapsed(!isNavbarCollapsed);
  };

  // Desktop Sidebar
  const SidebarContent = () => (
    <Flex
      direction="column"
      h="100vh"
      bg="purple.700"
      position="fixed"
      left={0}
      top={0}
      w={isNavbarCollapsed ? "60px" : "240px"}
      transition="width 0.3s ease"
      zIndex={999}
      boxShadow="2xl"
    >
      {/* Logo Section */}
      <Flex
        p={4}
        align="center"
        justify={isNavbarCollapsed ? "center" : "space-between"}
        borderBottom="1px solid"
        borderColor="whiteAlpha.300"
        position="relative"
        role="group"
      >
        <RouterLink to="/chat/new">
          <HStack spacing={3} justify={isNavbarCollapsed ? "center" : "flex-start"}>
            <Circle bg="purple.50" p={isNavbarCollapsed ? 0.5 : 0.5}>
              <AvatarWithMemo
                src={logo}
                animation={animation}
                name="Anko"
                size={isNavbarCollapsed ? "sm" : "sm"}
              />
            </Circle>
            {!isNavbarCollapsed && (
              <Text
                fontSize="xl"
                fontWeight="600"
                color="white"
                fontFamily="Exo 2"
                mb={0}
              >
                Anko
              </Text>
            )}
          </HStack>
        </RouterLink>

        {/* Expand button overlay when collapsed - shows on hover */}
        {isNavbarCollapsed && (
          <Tooltip
            label="Expand sidebar"
            placement="right"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="purple.600"
              opacity={0}
              _groupHover={{ opacity: 1, bg: "purple.700" }}
              transition="opacity 0.2s"
              cursor="pointer"
              onClick={toggleCollapse}
              zIndex={1}
            >
              <Icon as={TbLayoutSidebarLeftExpandFilled} boxSize={7} color="gray.100" />
            </Box>
          </Tooltip>
        )}

        {/* Collapse button when expanded */}
        {!isNavbarCollapsed && (
          <Tooltip
            label="Collapse sidebar"
            placement="bottom"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
          >
            <IconButton
              aria-label="Collapse sidebar"
              icon={<Icon as={TbLayoutSidebarRightExpandFilled} boxSize={7} />}
              size="sm"
              variant="ghost"
              color="gray.200"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={toggleCollapse}
            />
          </Tooltip>
        )}
      </Flex>

      {/* Navigation Links */}
      <VStack spacing={1} px={1} py={4} align="stretch">
        {navLinks.map((link, index) => {
          const NavContent = (
            <Flex
              as={RouterLink}
              to={link.to}
              key={index}
              align="center"
              justify={isNavbarCollapsed ? "center" : "flex-start"}
              px={isNavbarCollapsed ? 2 : 4}
              py={isNavbarCollapsed ? 3 : 2}
              borderRadius="lg"
              color="gray.100"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                bg: "whiteAlpha.200",
                color: "white",
                transform: "translateX(2px)",
                textDecoration: "none",
              }}
              _activeLink={{
                bg: "purple.600",
                color: "white",
                fontWeight: "bold",
                boxShadow: "md",
              }}
              onClick={() => handleNavLinkClick(link.to)}
            >
              <Icon
                as={link.iconComponent}
                boxSize={link.iconSize || 5} // Use custom size or default to 5
                minW={6}
                transition="transform 0.2s"
              />
              {!isNavbarCollapsed && (
                <Text ml={3} fontSize="md" fontWeight="500" mb={0}>
                  {link.text}
                </Text>
              )}
            </Flex>
          );

          // Wrap with Tooltip only when collapsed
          return isNavbarCollapsed ? (
            <Tooltip
              key={index}
              label={link.text}
              placement="right"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="sm"
            >
              {NavContent}
            </Tooltip>
          ) : (
            NavContent
          );
        })}
      </VStack>

      {/* Chat Sessions List - Only when not collapsed */}
      {!isNavbarCollapsed && (
        <>
          <Divider borderColor="whiteAlpha.300" />
          <Box px={3} py={0} flex={1} overflow="hidden" display="flex" flexDirection="column">
            <HStack spacing={2} mb={3}>
              <Icon as={IoChatbubbleOutline} boxSize={3.5} color="whiteAlpha.700" />
              <Text fontSize="xs" color="whiteAlpha.700" mb={0} fontWeight="medium" textAlign="left">
                Recent Chats
              </Text>
            </HStack>
            {isLoadingChatSessions ? (
              <Flex justify="center" py={4}>
                <Spinner size="sm" color="white" />
              </Flex>
            ) : chatSessionsList.length === 0 ? (
              <Text fontSize="sm" color="whiteAlpha.500" textAlign="center" p={1}>
                No chats yet
              </Text>
            ) : (
              <Box
                flex={1}
                overflowY="auto"
                pb={2}
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255,255,255,0.3)",
                    borderRadius: "2px",
                  },
                }}
              >
                {chatSessionsList.map((session) => {
                  const isSelected = currentChatId === session.id;
                  const isEditing = editingId === session.id;
                  return (
                    <HStack
                      key={session.id}
                      p={1}
                      bg={isSelected ? "purple.600" : "transparent"}
                      borderRadius="md"
                      _hover={{ bg: isSelected ? "purple.500" : "whiteAlpha.200" }}
                      mb={2}
                      spacing={2}
                      cursor={isEditing ? "default" : "pointer"}
                      onClick={() => !isEditing && selectChatSession(session.id!)}
                      role="group"
                    >
                      {isEditing ? (
                        <Input
                          size="xs"
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(session.id!);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          onBlur={() => setEditingId(null)}
                          onClick={(e) => e.stopPropagation()}
                          bg="whiteAlpha.200"
                          color="white"
                          border="1px solid"
                          borderColor="whiteAlpha.400"
                          _focus={{ borderColor: "purple.300", boxShadow: "none" }}
                          flex={1}
                        />
                      ) : (
                        <Text
                          flex={1}
                          fontSize="xs"
                          fontWeight={isSelected ? "semibold" : "normal"}
                          color="white"
                          noOfLines={1}
                          m={0}
                          textAlign="left"
                        >
                          {session.name || "Untitled"}
                        </Text>
                      )}
                      {!isEditing && (
                        <Menu placement="right-start">
                          <MenuButton
                            as={IconButton}
                            icon={<Icon as={FiMoreVertical} boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            color="whiteAlpha.600"
                            _hover={{ color: "white", bg: "whiteAlpha.300" }}
                            aria-label="Chat options"
                            minW="auto"
                            h="auto"
                            p={1}
                            onClick={(e) => e.stopPropagation()}
                            opacity={0}
                            _groupHover={{ opacity: 1 }}
                            transition="opacity 0.2s"
                          />
                          <MenuList minW="120px" bg="white" zIndex={1001}>
                            <MenuItem
                              icon={<Icon as={FiEdit2} boxSize={3.5} />}
                              fontSize="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditValue(session.name || "");
                                setEditingId(session.id!);
                              }}
                            >
                              Rename
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={FiTrash2} boxSize={4} color="red.500" />}
                              fontSize="xs"
                              color="red.500"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteChatSession(session.id!, ChatClientType.FRIDA);
                              }}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      )}
                    </HStack>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Bottom Section - User Menu */}
      <Box
        borderTop="1px solid"
        borderColor="whiteAlpha.300"
        p={2}
        mt="auto"
      >
        <Menu placement="top-end">
          <Tooltip
            label={username || "User Menu"}
            placement="right"
            hasArrow
            isDisabled={!isNavbarCollapsed}
          >
            <MenuButton
              as={Box}
              cursor="pointer"
              _hover={{ opacity: 0.8 }}
              transition="all 0.2s"
            >
              <Flex
                align="center"
                justify={isNavbarCollapsed ? "center" : "flex-start"}
                px={2}
                py={1}
                borderRadius="lg"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                <Avatar
                  size="sm"
                  name={username || "Anko"}
                  bg="green.700"
                  borderColor="#fff2f8"
                  borderWidth="1px"
                  color="white"
                  fontWeight="bold"
                />
                {!isNavbarCollapsed && (
                  <VStack align="flex-start" spacing={0} ml={3}>
                    <Text
                      color="white"
                      fontSize="sm"
                      fontWeight="semibold"
                      mb={0}
                      noOfLines={1}
                    >
                      {username || "Unknown User"}
                    </Text>
                  </VStack>
                )}
              </Flex>
            </MenuButton>
          </Tooltip>

          <MenuList zIndex={1000}>
            {userId === SUPERADMIN_USER_ID ? (
              <>

                <MenuItem
                  onClick={() => {
                    setIsNavbarCollapsed(true);
                    navigate("/eva");
                  }}
                  icon={<Icon as={MdSupportAgent} boxSize={6} color="green.500" />}
                  color="green.500"
                  fontWeight="bold"
                >
                  Eva
                </MenuItem>
                {/* <MenuItem
                  onClick={() => navigate("/old-eva")}
                  icon={<Icon as={MdSupportAgent} boxSize={6} color="gray.400" />}
                  color="gray.400"
                  fontWeight="bold"
                >
                  Old Eva
                </MenuItem> */}
                <Divider />
              </>
            ) : (
              <></>
            )}
            <MenuItem
              onClick={() => navigate("/user")}
              icon={<Icon as={FiSettings} boxSize={4} color="gray.500" />}
              color="black"
              fontWeight="semibold"
            >
              Settings
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              icon={<Icon as={TbLogout2} boxSize={5} color="red.500" />}
              color="black"
              fontWeight="semibold"
            >
              Logout
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </Flex>
  );

  // Mobile Drawer
  const MobileDrawerContent = () => (
    <Drawer
      isOpen={mobileDrawer.isOpen}
      placement="left"
      onClose={mobileDrawer.onClose}
    >
      <DrawerOverlay />
      <DrawerContent bg="purple.700">
        <DrawerBody py={4}>
          {/* Logo */}
          <Flex
            mb={6}
            align="center"
            justify="center"
            borderBottom="1px solid"
            borderColor="whiteAlpha.300"
            pb={4}
          >
            <RouterLink to="/" onClick={mobileDrawer.onClose}>
              <HStack spacing={3}>
                <Circle bg="purple.100" p={1}>
                  <AvatarWithMemo
                    src={logo}
                    animation={animation}
                    name="Anko"
                    size="sm"
                  />
                </Circle>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  color="white"
                  fontFamily="Exo 2"
                  mb={0}
                >
                  Anko
                </Text>
              </HStack>
            </RouterLink>
          </Flex>

          {/* Navigation Links */}
          <Stack spacing={2}>
            {navLinks.map((link, index) => (
              <Button
                key={index}
                as={RouterLink}
                to={link.to}
                onClick={() => {
                  handleNavLinkClick(link.to);
                  mobileDrawer.onClose();
                }}
                size="lg"
                variant="ghost"
                colorScheme="whiteAlpha"
                color="gray.200"
                justifyContent="flex-start"
                _hover={{ bg: "whiteAlpha.200", color: "white" }}
                _activeLink={{
                  bg: "purple.600",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                <HStack spacing={3}>
                  <Icon as={link.iconComponent} boxSize={link.iconSize || 5} />
                  <Text mb={0}>{link.text}</Text>
                </HStack>
              </Button>
            ))}

            {userId === SUPERADMIN_USER_ID ? (
              <>
                <Divider borderColor="whiteAlpha.300" my={2} />
                <Button
                  as={RouterLink}
                  to="/eva"
                  onClick={() => {
                    setIsNavbarCollapsed(true);
                    mobileDrawer.onClose();
                  }}
                  size="lg"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  color="gray.200"
                  justifyContent="flex-start"
                  _hover={{ bg: "whiteAlpha.200", color: "white" }}
                >
                  <HStack spacing={3}>
                    <Icon as={FiSettings} boxSize={5} />
                    <Text mb={0}>Eva</Text>
                  </HStack>
                </Button>
              </>
            ) : (
              <></>
            )}

            <Divider borderColor="whiteAlpha.300" my={2} />

            {/* User Info */}
            <Box
              p={3}
              borderRadius="lg"
              bg="whiteAlpha.200"
              color="white"
            >
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  name={username || "Anko"}
                  bg="green.700"
                  color="white"
                />
                <Text fontSize="sm" fontWeight="semibold" mb={0}>
                  {username || "Unknown User"}
                </Text>
              </HStack>
            </Box>

            {/* Settings Button */}
            <Button
              as={RouterLink}
              to="/user"
              onClick={mobileDrawer.onClose}
              size="lg"
              variant="ghost"
              colorScheme="whiteAlpha"
              color="gray.200"
              justifyContent="flex-start"
              _hover={{ bg: "whiteAlpha.200", color: "white" }}
            >
              <HStack spacing={3}>
                <Icon as={FiSettings} boxSize={5} />
                <Text mb={0}>Settings</Text>
              </HStack>
            </Button>

            {/* Logout Button */}
            <Button
              onClick={() => {
                mobileDrawer.onClose();
                handleLogout();
              }}
              colorScheme="red"
              size="lg"
              leftIcon={<Icon as={TbLogout2} />}
            >
              Logout
            </Button>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile */}
      <Box display={{ base: "none", md: "block" }}>
        <SidebarContent />
      </Box>

      {/* Mobile Header with Hamburger - Visible only on mobile */}
      <Box
        display={{ base: "block", md: "none" }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        bg="purple.700"
        zIndex={999}
        boxShadow="md"
      >
        <Flex px={4} py={3} align="center" justify="space-between">
          <RouterLink to="/">
            <HStack spacing={3}>
              <Circle bg="purple.100" p={1}>
                <AvatarWithMemo
                  src={logo}
                  animation={animation}
                  name="Anko"
                  size="xs"
                />
              </Circle>
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="white"
                fontFamily="Exo 2"
                mb={0}
              >
                Anko
              </Text>
            </HStack>
          </RouterLink>

          <IconButton
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            onClick={mobileDrawer.onOpen}
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
          />
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      <MobileDrawerContent />
    </>
  );
};

export default Navbar;
